import { useEffect, useState } from 'react';
import { useDesktopInit } from '@/hooks/useDesktopInit';
import { DesktopOnboarding } from './DesktopOnboarding';
import { StatusBar } from './StatusBar';
import { LicenseExpiredScreen } from './LicenseExpiredScreen';
import { VerificationReminder } from './VerificationReminder';
import { GracePeriodWarning } from './GracePeriodWarning';
import { useLicenseStore } from '@/stores/licenseStore';
import { useSyncStore } from '@/stores/syncStore';
import { verifyLicense, processSyncQueue, getSyncStats } from '@/lib/tauri';
import { useUser } from '@/hooks/useUser';
import { toast } from 'sonner';

interface DesktopWrapperProps {
  children: React.ReactNode;
}

/**
 * Wrapper component for desktop app
 * Handles:
 * - Initialization
 * - Onboarding
 * - License verification
 * - Auto-sync
 * - Status indicators
 */
export function DesktopWrapper({ children }: DesktopWrapperProps) {
  const initStatus = useDesktopInit();
  const { user } = useUser();

  const {
    licenseState,
    isBlocked,
    needsVerification,
    isInGracePeriod,
    graceDaysRemaining,
    setLicenseState,
  } = useLicenseStore();

  const { setSyncState, setStats } = useSyncStore();

  const [showVerificationReminder, setShowVerificationReminder] = useState(false);
  const [showGracePeriodWarning, setShowGracePeriodWarning] = useState(false);
  const [onboardingComplete, setOnboardingComplete] = useState(false);

  // Handle license verification on startup
  useEffect(() => {
    if (!initStatus.isDesktop || !initStatus.isInitialized) return;
    if (initStatus.needsOnboarding && !onboardingComplete) return;

    async function checkLicense() {
      try {
        console.log('🔍 Verifying license...');

        // Try online verification first
        try {
          const licenseInfo = await verifyLicense(true);
          console.log('✅ License verified online');
          useLicenseStore.getState().setLicense(licenseInfo);
        } catch (onlineError) {
          console.warn('⚠️ Online verification failed, trying offline...');

          // Fallback to offline verification
          const licenseInfo = await verifyLicense(false);
          console.log('✅ License verified offline');
          useLicenseStore.getState().setLicense(licenseInfo);
        }
      } catch (error: any) {
        console.error('❌ License verification failed:', error);
        toast.error('Échec de la vérification de la licence');
      }
    }

    checkLicense();
  }, [initStatus, onboardingComplete]);

  // Check if verification reminder should be shown
  useEffect(() => {
    if (!needsVerification()) return;

    if (isInGracePeriod()) {
      // In grace period - show urgent warning
      const days = graceDaysRemaining();
      if (days && days <= 3) {
        setShowGracePeriodWarning(true);
      }
    } else {
      // Not in grace period yet - show gentle reminder
      setShowVerificationReminder(true);
    }
  }, [needsVerification, isInGracePeriod, graceDaysRemaining]);

  // Auto-sync when online
  useEffect(() => {
    if (!initStatus.isDesktop || !user?.company_id) return;

    const handleOnline = async () => {
      console.log('🌐 Network online - starting sync...');
      setSyncState('syncing');

      try {
        const result = await processSyncQueue(user.company_id);
        console.log('✅ Sync completed:', result);

        // Update stats
        const stats = await getSyncStats(user.company_id);
        setStats(stats);

        if (result.processed > 0) {
          toast.success(`✅ ${result.processed} élément(s) synchronisé(s)`);
        }

        if (result.conflicts > 0) {
          toast.warning(`⚠️ ${result.conflicts} conflit(s) détecté(s)`);
        }

        setSyncState('synced');
      } catch (error: any) {
        console.error('❌ Sync failed:', error);
        setSyncState('error');
        toast.error('Échec de la synchronisation');
      }
    };

    window.addEventListener('online', handleOnline);

    // Sync on startup if online
    if (navigator.onLine) {
      handleOnline();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [initStatus.isDesktop, user?.company_id, setSyncState, setStats]);

  // Handle manual sync
  const handleManualSync = async () => {
    if (!user?.company_id) return;

    console.log('🔄 Manual sync triggered');
    setSyncState('syncing');

    try {
      const result = await processSyncQueue(user.company_id);
      const stats = await getSyncStats(user.company_id);
      setStats(stats);

      toast.success(`✅ ${result.processed} élément(s) synchronisé(s)`);
      setSyncState('synced');
    } catch (error: any) {
      console.error('❌ Manual sync failed:', error);
      setSyncState('error');
      toast.error('Échec de la synchronisation');
    }
  };

  // Handle pause/resume sync
  const handlePauseSync = () => {
    const { isPaused, setPaused } = useSyncStore.getState();
    setPaused(!isPaused);
    toast.info(isPaused ? 'Synchronisation reprise' : 'Synchronisation en pause');
  };

  // Handle license verification
  const handleVerifyLicense = async () => {
    setLicenseState('verifying');

    try {
      const licenseInfo = await verifyLicense(true);
      useLicenseStore.getState().setLicense(licenseInfo);

      toast.success('✅ Licence vérifiée avec succès');
      setShowVerificationReminder(false);
      setShowGracePeriodWarning(false);
    } catch (error: any) {
      console.error('❌ Verification failed:', error);
      toast.error('Échec de la vérification');
      setLicenseState('verification_needed');
    }
  };

  // Not desktop - render children normally
  if (!initStatus.isDesktop) {
    return <>{children}</>;
  }

  // Desktop initializing
  if (!initStatus.isInitialized) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-lg font-medium text-gray-700">
            Initialisation de leBusiness...
          </p>
          {initStatus.error && (
            <p className="text-sm text-red-600">{initStatus.error}</p>
          )}
        </div>
      </div>
    );
  }

  // Desktop needs onboarding
  if (initStatus.needsOnboarding && !onboardingComplete) {
    return <DesktopOnboarding onComplete={() => setOnboardingComplete(true)} />;
  }

  // License expired - blocking screen
  if (isBlocked) {
    return <LicenseExpiredScreen onRetryVerification={handleVerifyLicense} />;
  }

  // Normal desktop mode - status indicators now in DashboardLayout header
  return (
    <>
      {/* Main Content */}
      {children}

      {/* Verification Reminder Dialog */}
      <VerificationReminder
        open={showVerificationReminder}
        onVerify={handleVerifyLicense}
        onDismiss={() => setShowVerificationReminder(false)}
      />

      {/* Grace Period Warning Dialog */}
      <GracePeriodWarning
        open={showGracePeriodWarning}
        onVerify={handleVerifyLicense}
      />
    </>
  );
}
